import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Box,
  Text,
  Divider,
  HStack,
  Badge,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { RiSmartphoneLine, RiDownload2Line, RiShareForwardLine, RiAddBoxLine, RiAndroidLine, RiAppleLine, RiCheckLine } from "@remixicon/react";
import { MAIN_COLOR } from "../util/theme";

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const { i18n, t } = useTranslation();
  const [selectedLang, setSelectedLang] = useState<string>(i18n.language);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const toast = useToast();

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  // Reset selected language to current active language whenever modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedLang(i18n.language);
    }
  }, [isOpen, i18n.language]);

  const handleClose = () => {
    setSelectedLang(i18n.language);
    onClose();
  };

  const handleConfirm = () => {
    if (selectedLang !== i18n.language) {
      i18n.changeLanguage(selectedLang);
      localStorage.setItem("preferredLanguage", selectedLang);
    }
    onClose();
  };

  const handleAndroidInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        toast({
          title: t("app_install_started") || "앱 설치가 시작되었습니다!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      setDeferredPrompt(null);
    } else {
      toast({
        title: t("download_app_title") || "안드로이드 앱 설치 안내",
        description: t("android_guide_info") || "크롬/삼성인터넷 우측 상단 메뉴(⋮) ➔ '앱 설치' 또는 '홈 화면에 추가'를 누르시면 1초 만에 설치됩니다!",
        status: "info",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const languages = [
    { code: "ko", name: "한국어" },
    { code: "en", name: "English" },
    { code: "ja", name: "日本語" },
    { code: "zh", name: "中文" },
  ];

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
      {/* Light Blur Overlay without heavy dark tint */}
      <ModalOverlay backdropFilter="blur(3px)" bg="blackAlpha.300" />
      <ModalContent
        maxW={{ base: "calc(100vw - 32px)", sm: "460px" }}
        width={{ base: "calc(100vw - 32px)", sm: "460px" }}
        mx="auto"
        bg="white"
        borderRadius="24px"
        overflow="hidden"
        boxShadow="0 20px 40px rgba(0, 0, 0, 0.08)"
        border="1px solid"
        borderColor="gray.100"
      >
        <ModalHeader
          bg="white"
          py={4}
          px={{ base: 4, sm: 6 }}
          fontSize="md"
          fontWeight="bold"
          color="gray.800"
          borderBottom="1px solid"
          borderColor="gray.100"
          display="flex"
          alignItems="center"
          gap={2}
        >
          <span>⚙️</span>
          <span>{t("setting") || "설정"}</span>
        </ModalHeader>
        <ModalCloseButton mt={1} color="gray.500" borderRadius="full" />
        
        {/* Scrollable Body for future settings expansion */}
        <ModalBody py={{ base: 4, sm: 5 }} px={{ base: 4, sm: 6 }} maxH="75vh" overflowY="auto">
          <VStack spacing={5} align="stretch">
            
            {/* 1. App Download & Installation Section */}
            <Box
              bg="gray.50"
              p={{ base: 3.5, sm: 4 }}
              borderRadius="18px"
              border="1px solid"
              borderColor="gray.200"
            >
              <HStack justify="space-between" mb={3}>
                <HStack spacing={2}>
                  <RiSmartphoneLine size={18} color={MAIN_COLOR} />
                  <Text fontWeight="bold" fontSize={{ base: "xs", sm: "sm" }} color="gray.800">
                    {t("download_app_title") || "Nfimap 앱으로 다운로드"}
                  </Text>
                </HStack>
                <Badge bg="brand.purpleSoft" color="brand.main" borderRadius="full" px={2.5} py={0.5} fontSize="10px">
                  Android & iOS
                </Badge>
              </HStack>

              <Tabs variant="soft-rounded" colorScheme="purple" size="sm">
                <TabList bg="white" p={1} borderRadius="12px" border="1px solid" borderColor="gray.200">
                  <Tab borderRadius="8px" fontWeight="bold" fontSize="xs" flex={1} py={1.5} _selected={{ bg: "brand.main", color: "white" }}>
                    <HStack spacing={1.5}>
                      <RiAndroidLine size={15} />
                      <Text>Android</Text>
                    </HStack>
                  </Tab>
                  <Tab borderRadius="8px" fontWeight="bold" fontSize="xs" flex={1} py={1.5} _selected={{ bg: "brand.main", color: "white" }}>
                    <HStack spacing={1.5}>
                      <RiAppleLine size={15} />
                      <Text>iPhone (iOS)</Text>
                    </HStack>
                  </Tab>
                </TabList>

                <TabPanels mt={2.5}>
                  {/* Android Panel */}
                  <TabPanel p={0}>
                    <VStack align="stretch" spacing={2.5}>
                      <Text fontSize="xs" color="gray.600" lineHeight="1.5">
                        {t("android_desc") || "안드로이드 기기에서는 아래 버튼을 눌러 앱으로 바로 설치하세요!"}
                      </Text>
                      <Button
                        leftIcon={<RiDownload2Line size={16} />}
                        bg="brand.main"
                        color="white"
                        size="md"
                        h="42px"
                        borderRadius="12px"
                        fontWeight="bold"
                        fontSize="xs"
                        _hover={{ bg: "brand.hover" }}
                        onClick={handleAndroidInstall}
                      >
                        {t("download_android_btn") || "안드로이드 앱 바로 다운로드"}
                      </Button>
                    </VStack>
                  </TabPanel>

                  {/* iOS Panel */}
                  <TabPanel p={0}>
                    <VStack align="stretch" spacing={2} bg="white" p={3} borderRadius="12px" border="1px solid" borderColor="gray.200">
                      <Text fontSize="xs" fontWeight="bold" color="gray.800">
                        {t("ios_title") || "📱 iPhone / iPad 설치 방법:"}
                      </Text>
                      <VStack align="start" spacing={1.5} fontSize="xs" color="gray.700" pl={0.5}>
                        <HStack align="center" spacing={2}>
                          <RiShareForwardLine size={15} color={MAIN_COLOR} />
                          <Text lineHeight="1.4">{t("ios_step1") || "1. Chrome(크롬) 우측 공유(공유 아이콘) 클릭"}</Text>
                        </HStack>
                        <HStack align="center" spacing={2}>
                          <RiAddBoxLine size={15} color={MAIN_COLOR} />
                          <Text lineHeight="1.4">{t("ios_step2") || "2. '홈 화면에 추가 (+)' 선택"}</Text>
                        </HStack>
                        <Text color="gray.500" fontSize="11px" pl={6}>
                          {t("ios_step3") || "3. 오른쪽 상단 '추가' 누르면 앱처럼 사용 가능!"}
                        </Text>
                      </VStack>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>

            </Box>

            <Divider borderColor="gray.150" />

            {/* 2. Language Selection Section (Clean 1-Column List for Extension) */}
            <Box>
              <Text fontWeight="bold" fontSize="xs" color="gray.500" mb={2.5} textTransform="uppercase" letterSpacing="0.5px">
                {t("select_language") || "🌐 언어 선택 (Language)"}
              </Text>
              
              <VStack spacing={2} align="stretch">
                {languages.map((lang) => {
                  const isSelected = selectedLang === lang.code;
                  return (
                    <Button
                      key={lang.code}
                      size="md"
                      h="42px"
                      borderRadius="12px"
                      variant={isSelected ? "solid" : "outline"}
                      bg={isSelected ? "brand.main" : "white"}
                      color={isSelected ? "white" : "gray.700"}
                      borderColor={isSelected ? "brand.main" : "gray.200"}
                      fontWeight={isSelected ? "bold" : "medium"}
                      fontSize="xs"
                      justifyContent="space-between"
                      px={4}
                      _hover={{
                        bg: isSelected ? "brand.hover" : "gray.50",
                        borderColor: isSelected ? "brand.hover" : "gray.300",
                      }}
                      onClick={() => setSelectedLang(lang.code)}
                    >
                      <span>{lang.name}</span>
                      {isSelected && <RiCheckLine size={16} />}
                    </Button>
                  );
                })}
              </VStack>
            </Box>

          </VStack>
        </ModalBody>

        <ModalFooter bg="gray.50" py={3} px={{ base: 4, sm: 6 }} borderTop="1px solid" borderColor="gray.100">
          <Button
            w="100%"
            bg="brand.main"
            color="white"
            size="md"
            h="42px"
            borderRadius="12px"
            fontWeight="bold"
            fontSize="sm"
            _hover={{ bg: "brand.hover" }}
            onClick={handleConfirm}
          >
            {t("confirm") || "확인"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SettingsModal;
